import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Search,
  HandCoins,
  Banknote,
  X,
  Users,
  Loader2,
  Wallet,
  TrendingUp,
  TrendingDown,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });

// =============== Inline Advance Voucher Form (دفع / قبض only) ===============
const AdvanceVoucherForm = ({ employee, kind, onClose, onSaved }) => {
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const isOut = kind === "advance_out";
  const meta = isOut
    ? { label: "سند دفع سلفة", color: "#F5A623", icon: HandCoins, hint: "الموظف يأخذ سلفة الآن" }
    : { label: "سند قبض سلفة", color: "#7ED321", icon: Banknote, hint: "الموظف يسدّد جزءًا من سلفته" };
  const Icon = meta.icon;

  const submit = async () => {
    const amt = Number(amount);
    if (!amt || amt <= 0) { toast.error("أدخل مبلغًا صحيحًا"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/admin/employees/${employee.id}/vouchers`, {
        kind,
        amount: amt,
        note: note.trim(),
      });
      toast.success(`تم تسجيل ${meta.label}`);
      onSaved && onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "فشل");
    } finally { setBusy(false); }
  };

  return (
    <div
      className="fixed inset-0 z-[110] bg-black/60 flex items-center justify-center p-3"
      dir="rtl"
      data-testid={`advance-voucher-modal-${kind}`}
      onClick={onClose}
    >
      <div className="bg-white rounded-3xl w-full max-w-md p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center pb-3 mb-3 border-b-2 border-[#E4EBE6]">
          <h3 className="font-bold text-lg flex items-center gap-2" style={{ color: meta.color }}>
            <Icon className="w-5 h-5" />
            {meta.label}
          </h3>
          <button onClick={onClose} data-testid="advance-voucher-close">
            <X className="w-5 h-5 text-[#5C6E64]" />
          </button>
        </div>

        <div className="bg-[#F0F9FF] border border-[#3B82F6] rounded-xl px-3 py-2 mb-3">
          <p className="text-xs text-[#5C6E64]">الموظف</p>
          <p className="font-bold text-[#1E3A5F]">{employee.full_name}</p>
          <p className="text-[10px] text-[#5C6E64] mt-1">{meta.hint}</p>
        </div>

        <div className="bg-[#FFF8EC] border border-[#F5A623] rounded-xl px-3 py-2 mb-3 grid grid-cols-2 gap-2 text-center">
          <div>
            <p className="text-[9px] text-[#5C6E64]">السلفة المتبقّية</p>
            <p className="font-black text-sm text-[#a06e10]" dir="ltr">{fmt(employee.advance_remaining)}</p>
          </div>
          <div>
            <p className="text-[9px] text-[#5C6E64]">من سلفة</p>
            <p className="font-black text-sm text-[#a06e10]" dir="ltr">{fmt(employee.base_advance)}</p>
          </div>
        </div>

        <label className="text-xs font-bold text-[#1E3A5F] mb-1 block">المبلغ (د.ع)</label>
        <input
          type="number"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          data-testid="advance-voucher-amount"
          placeholder="0"
          className="w-full px-3 py-2 rounded-xl border-2 border-[#3B82F6] mb-3 text-lg font-bold"
          dir="ltr"
        />

        <label className="text-xs font-bold text-[#1E3A5F] mb-1 block">ملاحظة (اختياري)</label>
        <input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          data-testid="advance-voucher-note"
          placeholder="ملاحظة"
          className="w-full px-3 py-2 rounded-xl border border-[#E4EBE6] mb-4"
        />

        <button
          onClick={submit}
          disabled={busy}
          data-testid="advance-voucher-submit"
          className="w-full text-white font-bold py-2.5 rounded-full disabled:opacity-50"
          style={{ backgroundColor: meta.color }}
        >
          {busy ? "جاري الحفظ..." : "حفظ السند"}
        </button>
      </div>
    </div>
  );
};

// =============== Main Tab: Employee Advances Report ===============
const EmployeeAdvancesTab = () => {
  const [employees, setEmployees] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [voucherFor, setVoucherFor] = useState(null); // {employee, kind}
  const [refreshKey, setRefreshKey] = useState(0);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [eRes, sRes] = await Promise.all([
        axios.get(`${API}/admin/employees`),
        axios.get(`${API}/admin/employees-summary`),
      ]);
      setEmployees(eRes.data || []);
      setSummary(sRes.data);
    } catch { toast.error("فشل تحميل الموظفين"); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchAll(); }, [refreshKey]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return employees;
    return employees.filter((e) =>
      (e.full_name || "").toLowerCase().includes(q) ||
      (e.job_title || "").toLowerCase().includes(q) ||
      String(e.employee_number || "").toLowerCase().includes(q)
    );
  }, [employees, query]);

  if (loading) {
    return (
      <div className="text-center py-10">
        <Loader2 className="w-8 h-8 animate-spin inline text-[#1E3A5F]" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="employee-advances-tab" dir="rtl">
      {/* Top summary */}
      <div className="bg-gradient-to-l from-[#F5A623] to-[#dc9320] text-white rounded-2xl p-4">
        <div className="flex items-center gap-2 mb-2">
          <HandCoins className="w-5 h-5" />
          <h3 className="font-bold text-lg">كشف سلف الموظفين</h3>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <div>
            <p className="text-[10px] opacity-90">إجمالي السلف المتبقّية</p>
            <p className="text-xl font-black" dir="ltr">{fmt(summary?.total_advance_remaining)}</p>
          </div>
          <div>
            <p className="text-[10px] opacity-90">إجمالي المدفوع</p>
            <p className="text-xl font-black" dir="ltr">{fmt(summary?.total_advances_out)}</p>
          </div>
          <div>
            <p className="text-[10px] opacity-90">إجمالي المقبوض</p>
            <p className="text-xl font-black" dir="ltr">{fmt(summary?.total_advances_in)}</p>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C6E64]" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          data-testid="advances-search-input"
          placeholder="ابحث عن موظف بالاسم أو رقم الموظف..."
          className="w-full pr-9 pl-4 py-2.5 rounded-full bg-white border-2 border-[#3B82F6] text-sm focus:outline-none focus:ring-2 focus:ring-[#3B82F6]"
        />
      </div>

      {/* Employees list */}
      {filtered.length === 0 ? (
        <div className="bg-white border-2 border-dashed border-[#BAE6FD] rounded-2xl p-8 text-center">
          <Users className="w-12 h-12 mx-auto text-[#BAE6FD] mb-2" />
          <p className="text-[#5C6E64]">
            {employees.length === 0 ? "لا يوجد موظفون بعد" : "لا توجد نتائج لبحثك"}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((e) => (
            <motion.div
              key={e.id}
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              data-testid={`advance-emp-card-${e.id}`}
              className="bg-white rounded-2xl p-3 border-2 border-[#E4EBE6] shadow-sm"
            >
              <div className="flex items-center gap-3">
                {/* Avatar */}
                {e.photos?.[0] ? (
                  <img
                    src={`${API}/files/${e.photos[0]}`}
                    alt={e.full_name}
                    className="w-14 h-14 rounded-full object-cover border-2 border-[#3B82F6] shrink-0"
                  />
                ) : (
                  <div className="w-14 h-14 rounded-full bg-[#1E3A5F] text-white flex items-center justify-center shrink-0">
                    <Users className="w-6 h-6" />
                  </div>
                )}

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-[#111814] truncate">{e.full_name}</p>
                  <p className="text-[11px] text-[#5C6E64] truncate">
                    {e.job_title || "—"} · <span dir="ltr">#{e.employee_number || "—"}</span>
                  </p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-[10px] bg-[#F5A623]/15 text-[#a06e10] px-2 py-0.5 rounded-full font-bold">
                      سلفة متبقّية: <span dir="ltr">{fmt(e.advance_remaining)}</span>
                    </span>
                    <span className="text-[9px] text-[#5C6E64]" dir="ltr">
                      من {fmt(e.base_advance)}
                    </span>
                  </div>
                </div>

                {/* Voucher buttons */}
                <div className="flex flex-col gap-1 shrink-0">
                  <button
                    onClick={() => setVoucherFor({ employee: e, kind: "advance_out" })}
                    data-testid={`advance-out-btn-${e.id}`}
                    className="bg-[#F5A623] hover:bg-[#dc9320] text-white text-[10px] font-bold py-1.5 px-3 rounded-full flex items-center gap-1"
                    title="إعطاء سلفة جديدة للموظف"
                  >
                    <TrendingDown className="w-3 h-3" />
                    سند دفع سلفة
                  </button>
                  <button
                    onClick={() => setVoucherFor({ employee: e, kind: "advance_in" })}
                    data-testid={`advance-in-btn-${e.id}`}
                    className="bg-[#7ED321] hover:bg-[#6cb91d] text-white text-[10px] font-bold py-1.5 px-3 rounded-full flex items-center gap-1"
                    title="استلام دفعة من الموظف على حساب السلفة"
                  >
                    <TrendingUp className="w-3 h-3" />
                    سند قبض سلفة
                  </button>
                </div>
              </div>

              {/* Footer breakdown */}
              <div className="grid grid-cols-2 gap-2 mt-2 pt-2 border-t border-[#E4EBE6]">
                <div className="bg-[#FFF8EC] rounded-xl px-2 py-1 flex items-center justify-between text-[10px]">
                  <span className="text-[#a06e10] flex items-center gap-1">
                    <HandCoins className="w-3 h-3" /> مدفوع
                  </span>
                  <span className="font-bold text-[#a06e10]" dir="ltr">{fmt(e.total_advances_out)}</span>
                </div>
                <div className="bg-[#ECFDF5] rounded-xl px-2 py-1 flex items-center justify-between text-[10px]">
                  <span className="text-[#15803d] flex items-center gap-1">
                    <Wallet className="w-3 h-3" /> مقبوض
                  </span>
                  <span className="font-bold text-[#15803d]" dir="ltr">{fmt(e.total_advances_in)}</span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {voucherFor && (
        <AdvanceVoucherForm
          employee={voucherFor.employee}
          kind={voucherFor.kind}
          onClose={() => setVoucherFor(null)}
          onSaved={() => setRefreshKey((k) => k + 1)}
        />
      )}
    </div>
  );
};

export default EmployeeAdvancesTab;
